/**
 * useZoneLabelCollision
 *
 * Zoom-zone label collision avoidance. Restored from acd53470^ (#726), where
 * the pass was removed after it failed to converge on cypresscoveliving and
 * tripped React's max-update-depth guard, crashing the map.
 *
 * The loop is inherent to the design: the pass scores each zone against the
 * other zones *as currently rendered*, writes the winner into state, that
 * state changes the `htmlZoomZone--${orientation}` class, and a document-wide
 * MutationObserver on `class` bumps a tick that is in this effect's own
 * dependency list. Two zones can each pick the orientation that avoids the
 * other's previous rect and flip back — an A→B→A→B cycle that the old
 * `changed` check (a period-1 equality test) could not see.
 *
 * Three guards make termination unconditional. They are belt-and-braces on
 * purpose: any one of them alone would bound the loop, and the invariant here
 * is that the pass terminates on every input, however degenerate.
 *
 *   1. MAX_COLLISION_PASSES caps re-runs per input. On exhaustion the pass
 *      freezes and drops every override, so zones sit at the orientation
 *      configured on the term rather than at a half-settled state.
 *   2. Per-zone assignment history (last CYCLE_HISTORY_DEPTH orientations)
 *      detects a repeat, which is what a cycle looks like from inside one
 *      zone. A repeat is treated as converged and stops the loop. Longer
 *      cycles than the window are caught by guard 1.
 *   3. Rects are quantised to whole pixels before scoring, so sub-pixel
 *      getBoundingClientRect jitter cannot flip the covered-count tiebreak
 *      back and forth between otherwise identical layouts.
 *
 * Step 3 (the in-footprint ring search, restored in #768) moves the marker in
 * continuous space, so it is a stronger oscillation source than orientation
 * flipping alone. It needs no fourth guard: the shift is bounded to five fixed
 * radii inside the zone's own footprint bbox, the first clean hit wins so the
 * result is a function of the layout alone, and any position cycle it does
 * create still changes the orientation class that guard 2 watches — or, failing
 * that, still burns passes against guard 1's cap. Guard 2's token is
 * deliberately still the orientation alone: adding the offset to it would make
 * the guard fire later, not sooner, which is the wrong direction for a step
 * that moves things further.
 *
 * VERIFICATION: unit-verified only.
 * tests/unit/Map/useZoneLabelCollision.test.jsx covers termination, the absence
 * of a max-update-depth error, the base-orientation fallback on exhaustion, and
 * the freeze-at-last-pick on cycle detection.
 * tests/unit/Map/useZoneLabelCollisionFootprint.test.jsx covers step 3: that
 * the ring is searched in ascending radius order and the nearest clean centre
 * wins, that a candidate centre outside the footprint bbox is rejected, that a
 * zone with no footprint data and a zone whose every candidate is blocked both
 * fall back to the step 2 pick with no offset written, and that a layout whose
 * obstacles follow the shifted marker still settles.
 * tests/unit/Map/MapOverlayLabelsFootprints.test.jsx covers the producer: that
 * MapOverlayLabels builds the bboxes from place AND unit centres, keyed by
 * map_building_grouping uuid, and threads one stable Map identity through to
 * the zoom-zone MapLabels instances — which are rendered with no places and no
 * units, so they cannot build it themselves.
 * None of it has been checked in a browser: #745 means the dev Google Maps key
 * rejects both local origins, so no map renders locally. Visual behaviour —
 * whether the shifted marker looks right on a real community — is unverified
 * pending #745.
 */

import {
  useState, useEffect, useLayoutEffect, useRef,
} from 'react';

// Re-runs allowed per input before the pass gives up and freezes at the
// configured orientations. A settled layout needs one pass; a coordinating
// pair needs two or three. Anything past that is not converging.
const MAX_COLLISION_PASSES = 4;

// How many recent orientations to remember per zone. A value repeating inside
// this window means the zone is revisiting a state it has already been in:
// depth 2 catches period-1 (the only case the old code saw) and period-2 (the
// A→B→A→B flip that crashed cypresscoveliving) directly, and longer cycles
// still hit the pass cap below.
const CYCLE_HISTORY_DEPTH = 2;

// Empty results, frozen so the disabled/frozen paths hand out one identity
// and consumers never see a fresh object per render.
const EMPTY_OVERRIDES = Object.freeze({});

// Cardinals first, diagonals only as fallback when no cardinal is clear.
const CANDIDATE_ORIENTATIONS = [
  'bottom', 'top', 'right', 'left',
  'bottom-right', 'bottom-left', 'top-right', 'top-left',
];

const ZONE_TEXT_BUFFER = 8;

// Step 3 ring search. When no orientation is clean at the zone's base
// position, the whole marker (icon + label) is shifted within the zone's
// building footprint and every orientation re-scored at each candidate centre.
// Radii are tried in ascending order and the first clean hit wins, so the
// marker moves as little as it can: a nearer position is always preferred to a
// further one, whatever the angle. Twelve angles is a 30-degree step — fine
// enough to find a gap between labels, coarse enough that a whole ring is
// cheap.
const SHIFT_RING_RADII = [60, 110, 170, 240, 320];
const SHIFT_RING_ANGLES = 12;

// Need ~43+px between centers to clear two full-size zoom icons; 50 gives a
// small buffer. Cardinals first (cleaner visual), then diagonals.
const ICON_SHIFT = 50;
const DIAG_SHIFT = ICON_SHIFT * 0.7071; // ~35px each axis = ~50px hypot
const SHIFT_DIRECTIONS = [
  { dx: 0, dy: ICON_SHIFT },
  { dx: 0, dy: -ICON_SHIFT },
  { dx: ICON_SHIFT, dy: 0 },
  { dx: -ICON_SHIFT, dy: 0 },
  { dx: DIAG_SHIFT, dy: DIAG_SHIFT },
  { dx: -DIAG_SHIFT, dy: DIAG_SHIFT },
  { dx: DIAG_SHIFT, dy: -DIAG_SHIFT },
  { dx: -DIAG_SHIFT, dy: -DIAG_SHIFT },
];

// Guard 3. Every rect entering the scorer is rounded to whole pixels first.
// Browsers report fractional bounds that differ by ~0.01px between otherwise
// identical layouts, and the covered-count comparison below is an equality
// test — sub-pixel drift is enough to reverse it on alternate passes.
const quantise = (rect, pad = 0) => ({
  left: Math.round(rect.left) - pad,
  top: Math.round(rect.top) - pad,
  right: Math.round(rect.right) + pad,
  bottom: Math.round(rect.bottom) + pad,
  width: Math.round(rect.right - rect.left) + pad * 2,
  height: Math.round(rect.bottom - rect.top) + pad * 2,
});

const overlaps = (a, b) => !(
  a.right <= b.left
  || a.left >= b.right
  || a.bottom <= b.top
  || a.top >= b.bottom
);

// Shift a rect by (dx, dy) px without mutating the original. Width/height are
// carried through so downstream readers don't get NaN.
const shiftRect = (r, dx, dy) => ({
  left: r.left + dx,
  right: r.right + dx,
  top: r.top + dy,
  bottom: r.bottom + dy,
  width: r.right - r.left,
  height: r.bottom - r.top,
});

const rectFor = (iconRect, size, orientation, offset = 10, diagOffset = 4) => {
  const cx = iconRect.left + iconRect.width / 2;
  const cy = iconRect.top + iconRect.height / 2;
  switch (orientation) {
    case 'top':
      return {
        left: cx - size.width / 2,
        right: cx + size.width / 2,
        top: iconRect.top - size.height - offset,
        bottom: iconRect.top - offset,
      };
    case 'bottom':
      return {
        left: cx - size.width / 2,
        right: cx + size.width / 2,
        top: iconRect.bottom + offset,
        bottom: iconRect.bottom + offset + size.height,
      };
    case 'right':
      return {
        left: iconRect.right + offset,
        right: iconRect.right + offset + size.width,
        top: cy - size.height / 2,
        bottom: cy + size.height / 2,
      };
    case 'top-left':
      return {
        left: iconRect.left - size.width - diagOffset,
        right: iconRect.left - diagOffset,
        top: iconRect.top - size.height - diagOffset,
        bottom: iconRect.top - diagOffset,
      };
    case 'top-right':
      return {
        left: iconRect.right + diagOffset,
        right: iconRect.right + diagOffset + size.width,
        top: iconRect.top - size.height - diagOffset,
        bottom: iconRect.top - diagOffset,
      };
    case 'bottom-left':
      return {
        left: iconRect.left - size.width - diagOffset,
        right: iconRect.left - diagOffset,
        top: iconRect.bottom + diagOffset,
        bottom: iconRect.bottom + diagOffset + size.height,
      };
    case 'bottom-right':
      return {
        left: iconRect.right + diagOffset,
        right: iconRect.right + diagOffset + size.width,
        top: iconRect.bottom + diagOffset,
        bottom: iconRect.bottom + diagOffset + size.height,
      };
    case 'left':
    default:
      return {
        left: iconRect.left - size.width - offset,
        right: iconRect.left - offset,
        top: cy - size.height / 2,
        bottom: cy + size.height / 2,
      };
  }
};

// Text bounds rather than the full label box: the label has padding and a
// background extending past the text, and a slight box overlap is acceptable
// visually where a covered word is not.
const textRectOf = (el) => {
  const range = document.createRange();
  range.selectNodeContents(el);
  return range.getBoundingClientRect();
};

const findIconOffset = (iconRect, obstacles) => {
  if (!obstacles.some((dr) => overlaps(iconRect, dr))) return null;
  for (let i = 0; i < SHIFT_DIRECTIONS.length; i += 1) {
    const { dx, dy } = SHIFT_DIRECTIONS[i];
    const shifted = shiftRect(iconRect, dx, dy);
    if (!obstacles.some((dr) => overlaps(shifted, dr))) return { dx, dy };
  }
  return { dx: 0, dy: ICON_SHIFT };
};

/**
 * Resolve zoom-zone label overlaps by trying alternative orientations.
 *
 * @param {object} params
 * @param {object} params.containerRef Ref to the MapLabels container element.
 * @param {boolean} params.enabled Run the pass at all (off in bird's eye).
 * @param {string} params.inputSignature Changes when the layout inputs change;
 *   resets the pass budget. Deliberately excludes the self-triggered tick.
 * @param {Map} [params.zoneFootprints] uuid → `{ minX, minY, maxX, maxY }` bbox
 *   in SVG coordinates, from MapOverlayLabels. Step 3 will not move a zone
 *   marker outside its own building footprint; without this the step is skipped
 *   and an overlap is accepted, exactly as before.
 * @param {object} [params.viewBox] `{ x, y, width, height }` of the SVG, needed
 *   to convert a footprint bbox into the container pixel space the rest of the
 *   pass works in.
 * @returns {{ zoneOrientationOverrides: object, zoneIconOffsets: object,
 *   passCountRef: object }} passCountRef is the cap counter itself, exposed so
 *   the convergence tests can assert the run count settles.
 */
const useZoneLabelCollision = ({
  containerRef, enabled, inputSignature, zoneFootprints, viewBox,
}) => {
  // Keys are `zone:${uuid|id}`; values 'top' | 'bottom' | 'left' | 'right' |
  // the four diagonals.
  const [zoneOrientationOverrides, setZoneOrientationOverrides] = useState(EMPTY_OVERRIDES);
  // Per-zone `{ dx, dy }` px shift applied to the whole zone (icon + label)
  // when its dot would sit on top of another pin.
  const [zoneIconOffsets, setZoneIconOffsets] = useState(EMPTY_OVERRIDES);
  // Bumped by the MutationObserver below so the pass re-runs when zone label
  // DOM changes — including changes this pass itself caused, which is the
  // loop the guards bound.
  const [collisionTick, setCollisionTick] = useState(0);

  // Mirror the applied offsets so the pass can back out the already-applied
  // transform before measuring. Without this it measures the *shifted* icon,
  // sees no collision, removes the offset, and flip-flops.
  const zoneIconOffsetsRef = useRef(zoneIconOffsets);
  zoneIconOffsetsRef.current = zoneIconOffsets;

  // Per-zone orientation history that survives zone unmount, so returning to
  // the same zoom restores the same side rather than re-picking from scratch.
  const orientationHistoryRef = useRef({});

  // Guard 1 + 2 state.
  const passCountRef = useRef(0);
  const frozenRef = useRef(false);
  const assignmentHistoryRef = useRef({});
  const signatureRef = useRef(null);

  useLayoutEffect(() => {
    if (!enabled) return;

    // A genuine input change (zones, viewport, place set) earns a fresh
    // budget. The tick does not — it is this effect's own output coming back
    // round, and resetting on it would defeat the cap entirely.
    if (signatureRef.current !== inputSignature) {
      signatureRef.current = inputSignature;
      passCountRef.current = 0;
      frozenRef.current = false;
      assignmentHistoryRef.current = {};
    }

    // Frozen: either the budget ran out or a cycle was seen. Never schedule
    // another pass for this input.
    if (frozenRef.current) return;

    passCountRef.current += 1;

    // Guard 1. Budget exhausted — drop every override so each zone renders at
    // the orientation configured on its term. A half-settled layout is worse
    // than the configured one: it is arbitrary, and it is whichever side the
    // last aborted pass happened to be on.
    //
    // This resets where guard 2 freezes, and the difference is what each guard
    // knows. Here no repeat was ever seen: every pass picked a fresh
    // orientation, so the sequence was still exploring when the budget ran
    // out, and the last pick is just where the counter happened to stop — no
    // evidence it is any better than the configured side. Guard 2 has the
    // opposite evidence; see there.
    if (passCountRef.current > MAX_COLLISION_PASSES) {
      frozenRef.current = true;
      setZoneOrientationOverrides((prev) => (
        Object.keys(prev).length === 0 ? prev : EMPTY_OVERRIDES
      ));
      setZoneIconOffsets((prev) => (
        Object.keys(prev).length === 0 ? prev : EMPTY_OVERRIDES
      ));
      return;
    }

    const container = containerRef.current;
    if (!container) return;
    const zoneEls = Array.from(container.querySelectorAll('[data-zone-key]'));
    if (zoneEls.length === 0) return;

    // Place labels are rendered by a sibling MapLabels instance (separate
    // container), so query the whole document rather than just our container.
    const collect = (selector, pad = 0) => Array.from(document.querySelectorAll(selector))
      .filter((el) => el.offsetWidth > 0 && el.offsetHeight > 0)
      .map((el) => quantise(el.getBoundingClientRect(), pad));

    const placeDotRects = collect('.htmlPlaceMarker__dot, .htmlClusterBadge__icon');
    // Cluster icons (43px) are a real visual obstacle for zone labels, unlike
    // place dots (14px), so they are tracked separately and only they join the
    // hard-obstacle set.
    const clusterIconRects = collect('.htmlClusterBadge__icon', 10);
    const clusterLabelRects = Array.from(document.querySelectorAll('.htmlClusterBadge__label'))
      .filter((el) => el.offsetWidth > 0 && el.offsetHeight > 0)
      .map((el) => quantise(textRectOf(el), 8));
    const placeLabelInfo = Array.from(document.querySelectorAll('.htmlPlaceLabel'))
      .filter((el) => el.offsetWidth > 0 && el.offsetHeight > 0 && el.dataset.placeUuid)
      .map((el) => ({
        uuid: el.dataset.placeUuid,
        rect: quantise(textRectOf(el), 6),
      }));
    // Unit labels are deliberately excluded: they fill most of a building tile
    // and would block almost every position.

    const appliedOffsets = zoneIconOffsetsRef.current;
    const zoneIconRectsByKey = {};
    const zoneLabelRectsByKey = {};
    const zoneTextRectsByKey = {};
    const zoneBaseOrientationByKey = {};
    const zoneLabelSizeByKey = {};

    zoneEls.forEach((el) => {
      const k = el.dataset.zoneKey;
      const iconEl = el.querySelector('.htmlZoomZone__icon, .htmlZoomZone__dot');
      // Include fade-pending labels; only EXCLUDE `--hidden`, which means the
      // user minimized an asDot zone and its label will not appear.
      const labelEl = el.querySelector(
        '.htmlZoomZone__label:not(.htmlZoomZone__label--hidden)',
      );
      if (!iconEl || iconEl.offsetWidth <= 0 || iconEl.offsetHeight <= 0) return;
      const measured = quantise(iconEl.getBoundingClientRect());
      const appliedOffset = appliedOffsets[k];
      zoneIconRectsByKey[k] = appliedOffset
        ? shiftRect(measured, -appliedOffset.dx, -appliedOffset.dy)
        : measured;
      zoneBaseOrientationByKey[k] = el.dataset.zoneOrientation || 'bottom';
      if (labelEl && labelEl.offsetWidth > 0 && labelEl.offsetHeight > 0) {
        const tr = quantise(textRectOf(labelEl));
        zoneLabelSizeByKey[k] = { width: tr.width, height: tr.height };
        zoneTextRectsByKey[k] = tr;
        // Obstacle rect at the currently rendered position — other zones need
        // to avoid where this label actually IS, not where base orientation
        // would put it.
        zoneLabelRectsByKey[k] = quantise(labelEl.getBoundingClientRect());
      }
    });

    const nextOrientations = {};
    const nextOffsets = {};

    // SVG → container-px conversion for the step 3 footprint bounds. Same math
    // as svgToPercent, then scaled into pixels against the container rect,
    // measured once per pass.
    const containerRect = container.getBoundingClientRect();
    const svgPointToContainerPx = (svgX, svgY) => {
      if (!viewBox?.width || !viewBox?.height) return null;
      const pctX = (svgX - viewBox.x) / viewBox.width;
      const pctY = (svgY - viewBox.y) / viewBox.height;
      return {
        x: containerRect.left + pctX * containerRect.width,
        y: containerRect.top + pctY * containerRect.height,
      };
    };

    zoneEls.forEach((zoneEl) => {
      const key = zoneEl.dataset.zoneKey;
      const baseOrientation = zoneBaseOrientationByKey[key] || 'bottom';
      const rawIconRect = zoneIconRectsByKey[key];
      if (!rawIconRect) return;

      // Per-zone obstacle lists exclude self so a zone never collides with its
      // own icon or label.
      const otherZoneIconRects = Object.entries(zoneIconRectsByKey)
        .filter(([k]) => k !== key)
        .map(([k, r]) => {
          const off = appliedOffsets[k];
          return off ? shiftRect(r, off.dx, off.dy) : r;
        });
      const otherZoneLabelRects = Object.entries(zoneLabelRectsByKey)
        .filter(([k]) => k !== key)
        .map(([, r]) => r);
      const dotObstacles = [...placeDotRects, ...otherZoneIconRects];
      // Hard obstacles must be avoided entirely. Place dots are excluded —
      // they are small and a zone label legitimately sits over them in dense
      // areas. Place labels are soft and scored below.
      const hardObstacles = [
        ...clusterIconRects,
        ...clusterLabelRects,
        ...otherZoneIconRects,
        ...otherZoneLabelRects,
        ...placeLabelInfo.map((info) => info.rect),
      ];

      // Step 1: shift the whole zone if its raw position overlaps a place dot
      // or another zone's icon.
      const offset = findIconOffset(rawIconRect, dotObstacles);
      if (offset) nextOffsets[key] = offset;
      const iconRect = offset ? shiftRect(rawIconRect, offset.dx, offset.dy) : rawIconRect;

      const size = zoneLabelSizeByKey[key];
      if (!size) {
        // Hidden label (asDot minimized): keep the prior orientation and
        // offset so the label fades in place instead of sliding.
        const stickyOri = orientationHistoryRef.current[key];
        if (stickyOri && stickyOri !== baseOrientation) nextOrientations[key] = stickyOri;
        if (appliedOffsets[key]) nextOffsets[key] = appliedOffsets[key];
        return;
      }

      // Step 2: pick a label orientation that avoids the obstacles, anchored
      // on the (possibly shifted) icon. Prefer fewer zone overlaps, then fewer
      // covered place labels, then closer to the zone's own pin.
      const tryOrder = [
        baseOrientation,
        ...CANDIDATE_ORIENTATIONS.filter((o) => o !== baseOrientation),
      ];
      const iconCenterX = iconRect.left + iconRect.width / 2;
      const iconCenterY = iconRect.top + iconRect.height / 2;

      const scoreOrientation = (candidate) => {
        const candidateRect = rectFor(iconRect, size, candidate);
        if (hardObstacles.some((pr) => overlaps(candidateRect, pr))) return null;
        const covered = placeLabelInfo.filter((info) => overlaps(candidateRect, info.rect));
        let zoneOverlapCount = 0;
        Object.entries(zoneTextRectsByKey)
          .filter(([k]) => k !== key)
          .forEach(([, textRect]) => {
            if (!textRect) return;
            const buffered = {
              left: textRect.left - ZONE_TEXT_BUFFER,
              top: textRect.top - ZONE_TEXT_BUFFER,
              right: textRect.right + ZONE_TEXT_BUFFER,
              bottom: textRect.bottom + ZONE_TEXT_BUFFER,
            };
            if (overlaps(candidateRect, buffered)) zoneOverlapCount += 1;
          });
        const labelCenterX = candidateRect.left + candidateRect.width / 2;
        const labelCenterY = candidateRect.top + candidateRect.height / 2;
        const dist = Math.sqrt(
          (labelCenterX - iconCenterX) ** 2 + (labelCenterY - iconCenterY) ** 2,
        );
        return { covered, zoneOverlapCount, dist };
      };

      let bestOrientation = null;
      let bestScore = null;

      for (let i = 0; i < tryOrder.length; i += 1) {
        const candidate = tryOrder[i];
        const score = scoreOrientation(candidate);
        if (score) {
          const better = bestScore === null
            || score.zoneOverlapCount < bestScore.zoneOverlapCount
            || (score.zoneOverlapCount === bestScore.zoneOverlapCount
              && score.covered.length < bestScore.covered.length)
            || (score.zoneOverlapCount === bestScore.zoneOverlapCount
              && score.covered.length === bestScore.covered.length
              && score.dist < bestScore.dist);
          if (better) {
            bestOrientation = candidate;
            bestScore = score;
          }
        }
      }

      // The historical pick wins ties, so a zone that already has a clean side
      // keeps it rather than swapping to an equally good one.
      const historical = orientationHistoryRef.current[key];
      const historicalScore = historical ? scoreOrientation(historical) : null;
      const bestCount = bestScore ? bestScore.covered.length : Infinity;
      const historicalCount = historicalScore ? historicalScore.covered.length : Infinity;

      let finalOrientation;
      if (historicalScore
        && (historicalCount < bestCount
          || (historicalCount === bestCount && historicalScore.dist <= bestScore.dist))) {
        finalOrientation = historical;
      } else if (bestOrientation) {
        finalOrientation = bestOrientation;
      } else {
        // No orientation clears the hard obstacles — accept the configured one
        // and let the label overlap.
        finalOrientation = baseOrientation;
      }

      orientationHistoryRef.current[key] = finalOrientation;
      if (finalOrientation !== baseOrientation) nextOrientations[key] = finalOrientation;

      // Later zones in this same pass see the updated rect, so they can
      // coordinate rather than all optimising against the initial positions.
      const chosenRect = rectFor(iconRect, size, finalOrientation);
      zoneLabelRectsByKey[key] = chosenRect;
      zoneTextRectsByKey[key] = chosenRect;

      const finalCovered = placeLabelInfo.filter((info) => overlaps(chosenRect, info.rect));
      // Clean orientation found — nothing left to avoid, so no shift.
      if (finalCovered.length === 0) return;

      // Step 3: no orientation at this position is clean, so try moving the
      // whole marker (icon + label) inside the zone's building footprint and
      // re-score every orientation at each candidate centre. Ring search
      // outward from the current icon centre; the first clean hit wins, which
      // makes the result a function of the layout alone — no tie-breaking, no
      // dependence on which zone was visited first.
      const zoneUuid = key.replace(/^zone:/, '');
      const footprint = zoneFootprints?.get?.(zoneUuid);
      // No footprint data for this zone: accept the overlap rather than move
      // the marker off its building.
      if (!footprint) return;
      const fpTL = svgPointToContainerPx(footprint.minX, footprint.minY);
      const fpBR = svgPointToContainerPx(footprint.maxX, footprint.maxY);
      if (!fpTL || !fpBR) return;

      const iconCx = iconRect.left + iconRect.width / 2;
      const iconCy = iconRect.top + iconRect.height / 2;
      let shiftFound = null;
      for (let ri = 0; ri < SHIFT_RING_RADII.length && !shiftFound; ri += 1) {
        const radius = SHIFT_RING_RADII[ri];
        for (let ai = 0; ai < SHIFT_RING_ANGLES && !shiftFound; ai += 1) {
          const angle = (ai * Math.PI * 2) / SHIFT_RING_ANGLES;
          const dx = Math.cos(angle) * radius;
          const dy = Math.sin(angle) * radius;
          const candCx = iconCx + dx;
          const candCy = iconCy + dy;
          // Stay on the building. A zone marker outside its own footprint
          // points at the wrong place, which is worse than an overlap.
          if (candCx >= fpTL.x && candCx <= fpBR.x && candCy >= fpTL.y && candCy <= fpBR.y) {
            const candIcon = shiftRect(iconRect, dx, dy);
            if (!dotObstacles.some((d) => overlaps(candIcon, d))) {
              for (let oi = 0; oi < tryOrder.length && !shiftFound; oi += 1) {
                const ori = tryOrder[oi];
                const candLabel = rectFor(candIcon, size, ori);
                const clean = !hardObstacles.some((h) => overlaps(candLabel, h))
                  && !placeLabelInfo.some((info) => overlaps(candLabel, info.rect));
                if (clean) shiftFound = { dx, dy, orientation: ori };
              }
            }
          }
        }
      }
      // Every candidate centre was off the footprint, on a dot, or still
      // covered at every orientation. Leave the step 2 pick standing and let
      // the label overlap: a visible-but-overlapped label beats one parked
      // somewhere arbitrary.
      if (!shiftFound) return;

      // Combine with the step 1 icon offset, which is already in nextOffsets.
      nextOffsets[key] = offset
        ? { dx: offset.dx + shiftFound.dx, dy: offset.dy + shiftFound.dy }
        : { dx: shiftFound.dx, dy: shiftFound.dy };
      orientationHistoryRef.current[key] = shiftFound.orientation;
      if (shiftFound.orientation === baseOrientation) {
        delete nextOrientations[key];
      } else {
        nextOrientations[key] = shiftFound.orientation;
      }
      const shiftedRect = rectFor(
        shiftRect(iconRect, shiftFound.dx, shiftFound.dy),
        size,
        shiftFound.orientation,
      );
      zoneLabelRectsByKey[key] = shiftedRect;
      zoneTextRectsByKey[key] = shiftedRect;
    });

    // Guard 2. Record this pass's assignment per zone and look for a repeat
    // inside the window. A zone returning to an orientation it has recently
    // held is the signature of a cycle of any period — the old code only
    // compared against the immediately-previous value, which sees period 1 and
    // nothing else.
    //
    // Unlike guard 1 this FREEZES at the last pick rather than resetting to
    // baseOrientation, and deliberately: a repeat proves the layout has no
    // fixed point and that the assignments have closed into a small repeating
    // set, every member of which is a completed pass's best-scoring answer.
    // The current one is a member of that set; baseOrientation is the side the
    // scorer has already rejected on each of those passes. Resetting would
    // trade a known-good pick for a known-bad one. Guard 1 has no such
    // evidence, so it prefers the configured side. Pinned by the
    // cycle-freeze test in tests/unit/Map/useZoneLabelCollision.test.jsx.
    let cycleDetected = false;
    zoneEls.forEach((zoneEl) => {
      const key = zoneEl.dataset.zoneKey;
      const chosen = nextOrientations[key] || zoneBaseOrientationByKey[key] || 'bottom';
      const history = assignmentHistoryRef.current[key] || [];
      if (history.includes(chosen)) cycleDetected = true;
      assignmentHistoryRef.current[key] = [chosen, ...history].slice(0, CYCLE_HISTORY_DEPTH);
    });
    if (cycleDetected) frozenRef.current = true;

    setZoneOrientationOverrides((prev) => {
      const merged = { ...prev };
      let changed = false;
      Object.entries(nextOrientations).forEach(([k, v]) => {
        if (merged[k] !== v) { merged[k] = v; changed = true; }
      });
      Object.keys(merged).forEach((k) => {
        if (!(k in nextOrientations)) { delete merged[k]; changed = true; }
      });
      return changed ? merged : prev;
    });

    setZoneIconOffsets((prev) => {
      const merged = { ...prev };
      let changed = false;
      Object.entries(nextOffsets).forEach(([k, v]) => {
        const existing = merged[k];
        if (!existing || existing.dx !== v.dx || existing.dy !== v.dy) {
          merged[k] = v;
          changed = true;
        }
      });
      Object.keys(merged).forEach((k) => {
        if (!(k in nextOffsets)) { delete merged[k]; changed = true; }
      });
      return changed ? merged : prev;
    });
  }, [containerRef, enabled, inputSignature, collisionTick, zoneFootprints, viewBox]);

  // Re-run when zone label DOM appears, disappears or changes class, debounced
  // to one re-run per frame. Place label and dot mutations are deliberately
  // not watched — toggling an individual place label should not relayout the
  // zone level. The tick is only acted on while the pass is unfrozen.
  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof MutationObserver === 'undefined') return undefined;
    let pendingFrame = null;
    const schedule = () => {
      if (pendingFrame !== null) return;
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = null;
        setCollisionTick((t) => t + 1);
      });
    };
    // The label element itself for add/remove, and the zone wrapper for class
    // changes — the `htmlZoomZone--${orientation}` class this pass writes
    // lands on the wrapper, not on the label.
    const labelLike = (node) => !!(node?.classList
      && (node.classList.contains('htmlZoomZone__label')
        || node.dataset?.zoneKey));
    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((m) => {
        if (m.type === 'childList') {
          return [...m.addedNodes, ...m.removedNodes].some(labelLike);
        }
        if (m.type === 'attributes') return labelLike(m.target);
        return false;
      });
      if (relevant) schedule();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => {
      observer.disconnect();
      if (pendingFrame !== null) cancelAnimationFrame(pendingFrame);
    };
  }, [enabled]);

  return { zoneOrientationOverrides, zoneIconOffsets, passCountRef };
};

export default useZoneLabelCollision;
export {
  MAX_COLLISION_PASSES, CYCLE_HISTORY_DEPTH, EMPTY_OVERRIDES,
  SHIFT_RING_RADII, SHIFT_RING_ANGLES,
};
